// Kit visual do painel (redesign 2026) — usado por todas as telas internas
// pra manter cabecalho de pagina, cartoes, botoes e etiquetas iguais.
import React, { useRef, useState } from "react";
import { Box, Button, InputBase, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { CORES, iniciais } from "../../utils/participante";

// Fundo cinza + coluna central + "eyebrow" e titulo da pagina, com acoes a direita.
export const Pagina = ({ eyebrow, titulo, descricao, acoes, largura = 1040, children }) => (
  <Box sx={{ flex: 1, width: "100%", alignSelf: "stretch", backgroundColor: CORES.fundo }}>
    <Box sx={{ maxWidth: largura, mx: "auto", px: { xs: 2, md: 3 }, pt: { xs: 3, md: 3.5 }, pb: 6 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, color: CORES.textoSuave, textTransform: "uppercase" }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            component="h1"
            sx={{ fontSize: { xs: 22, md: 25 }, fontWeight: 800, color: CORES.navy, lineHeight: 1.2 }}
          >
            {titulo}
          </Typography>
          {descricao && (
            <Typography sx={{ fontSize: 13, color: CORES.textoSuave, mt: 0.5, maxWidth: 620 }}>{descricao}</Typography>
          )}
        </Box>
        {acoes && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>{acoes}</Box>}
      </Box>
      {children}
    </Box>
  </Box>
);

export const Cartao = ({ titulo, descricao, acao, children, sx, semPadding }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      border: `1px solid ${CORES.borda}`,
      borderRadius: "10px",
      overflow: "hidden",
      ...sx,
    }}
  >
    {(titulo || acao) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          px: 2.25,
          py: 1.75,
          borderBottom: `1px solid ${CORES.borda}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CORES.texto }}>{titulo}</Typography>
          {descricao && (
            <Typography sx={{ fontSize: 12, color: CORES.textoSuave, mt: 0.25 }}>{descricao}</Typography>
          )}
        </Box>
        {acao}
      </Box>
    )}
    <Box sx={semPadding ? undefined : { p: 2.25 }}>{children}</Box>
  </Box>
);

export const CardResumo = ({ titulo, valor, detalhe, progresso, cor }) => (
  <Box sx={{ backgroundColor: "#fff", border: `1px solid ${CORES.borda}`, borderRadius: "10px", px: 2.25, py: 2 }}>
    <Typography sx={{ fontSize: 12, color: CORES.navyClaro, fontWeight: 500 }}>{titulo}</Typography>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.75 }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: CORES.navy, lineHeight: 1 }}>{valor}</Typography>
      {detalhe && <Typography sx={{ fontSize: 11.5, color: CORES.textoSuave }}>{detalhe}</Typography>}
    </Box>
    {progresso !== undefined && (
      <Box sx={{ mt: 1.5, height: 3, borderRadius: 2, backgroundColor: "#edf0f4", overflow: "hidden" }}>
        <Box sx={{ width: `${Math.min(progresso, 100)}%`, height: "100%", backgroundColor: cor, transition: "width .4s" }} />
      </Box>
    )}
  </Box>
);

// Botao compacto (34px) usado nas acoes do topo das paginas.
export const BotaoAcao = ({ children, icon, primario, sx, ...props }) => (
  <Button
    startIcon={icon}
    {...props}
    sx={{
      height: 34,
      px: 1.75,
      py: 0,
      fontSize: 12.5,
      fontWeight: 600,
      borderRadius: "6px",
      "& .MuiButton-startIcon svg": { fontSize: 16 },
      ...(primario
        ? { backgroundColor: CORES.navyClaro, color: "#fff", "&:hover": { backgroundColor: CORES.navy } }
        : {
            backgroundColor: "#fff",
            color: CORES.texto,
            border: `1px solid ${CORES.borda}`,
            "&:hover": { backgroundColor: "#f6f8fa" },
          }),
      ...sx,
    }}
  >
    {children}
  </Button>
);

export const Etiqueta = ({ children, tom = "neutro" }) => {
  const tons = {
    verde: { cor: CORES.verde, fundo: CORES.verdeFundo },
    ambar: { cor: "#9a6a00", fundo: CORES.ambarFundo },
    vermelho: { cor: CORES.vermelho, fundo: CORES.vermelhoFundo },
    azul: { cor: CORES.navyClaro, fundo: "#e8f0f8" },
    neutro: { cor: "#4a5868", fundo: "#eef1f4" },
  };
  const { cor, fundo } = tons[tom] || tons.neutro;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: "4px",
        fontSize: 11,
        fontWeight: 600,
        color: cor,
        backgroundColor: fundo,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
};

export const AvatarIniciais = ({ nome, tom = "azul", tamanho = 28 }) => (
  <Box
    sx={{
      width: tamanho,
      height: tamanho,
      borderRadius: "50%",
      backgroundColor: tom === "verde" ? CORES.verdeFundo : "#e8eef5",
      color: tom === "verde" ? CORES.verde : CORES.navyClaro,
      display: "grid",
      placeItems: "center",
      fontSize: tamanho * 0.37,
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    {iniciais(nome)}
  </Box>
);

export const CampoBusca = ({ value, onChange, placeholder, sx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      height: 34,
      px: 1.5,
      border: `1px solid ${CORES.borda}`,
      borderRadius: "6px",
      backgroundColor: "#fff",
      "&:focus-within": { borderColor: CORES.navyClaro },
      ...sx,
    }}
  >
    <SearchIcon sx={{ fontSize: 17, color: "#7d8a98" }} />
    <InputBase
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ flex: 1, fontSize: 12.5 }}
      inputProps={{ "aria-label": placeholder }}
    />
  </Box>
);

// Area pontilhada pra escolher/arrastar arquivo (importacoes).
export const AreaArquivo = ({ arquivo, onArquivo, accept, rotulo, dica }) => {
  const inputRef = useRef(null);
  const [arrastando, setArrastando] = useState(false);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setArrastando(true);
      }}
      onDragLeave={() => setArrastando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastando(false);
        const solto = e.dataTransfer.files?.[0];
        if (solto) onArquivo(solto);
      }}
      sx={{
        cursor: "pointer",
        border: `1.5px dashed ${arrastando ? CORES.navyClaro : "#cdd5df"}`,
        backgroundColor: arrastando ? "#f3f7fb" : "#fafbfc",
        borderRadius: "10px",
        px: 3,
        py: 4,
        textAlign: "center",
        transition: "border-color .15s, background-color .15s",
        "&:hover, &:focus-visible": { borderColor: CORES.navyClaro, outline: "none" },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => {
          onArquivo(e.target.files?.[0] || null);
          e.target.value = "";
        }}
      />
      <UploadFileOutlinedIcon sx={{ fontSize: 30, color: arquivo ? CORES.verde : "#6b7a8a" }} />
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CORES.texto, mt: 1 }}>
        {arquivo ? arquivo.name : rotulo}
      </Typography>
      <Typography sx={{ fontSize: 12, color: CORES.textoSuave, mt: 0.5 }}>
        {arquivo ? "Clique para trocar o arquivo" : dica}
      </Typography>
    </Box>
  );
};

// Estilos de tabela compartilhados (cabecalho em caixa alta, linhas finas).
export const celulaCabecalho = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "#4a5868",
  backgroundColor: "#fff",
  borderBottom: `1px solid ${CORES.borda}`,
  py: 1.25,
  whiteSpace: "nowrap",
};

export const celula = {
  fontSize: 12.5,
  color: CORES.texto,
  borderBottom: `1px solid ${CORES.borda}`,
  py: 1.25,
};
